import React, { useState } from 'react';
import { XIcon, SearchIcon, StarIcon, DownloadIcon, FilterIcon } from 'lucide-react';
interface TemplateBrowserModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function TemplateBrowserModal({
  isOpen,
  onClose
}: TemplateBrowserModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  if (!isOpen) return null;
  const templates = [{
    id: 1,
    name: 'High-Intent Blitz',
    category: 'ABM',
    touchpoints: 8,
    duration: '14 days',
    rating: 4.9,
    uses: 1247,
    description: 'Multi-channel sequence for high-intent accounts'
  }, {
    id: 2,
    name: 'Executive Nurture',
    category: 'ABM',
    touchpoints: 12,
    duration: '21 days',
    rating: 4.8,
    uses: 892,
    description: 'Long-term engagement for C-level prospects'
  }, {
    id: 3,
    name: 'PLG Activation',
    category: 'PLG',
    touchpoints: 6,
    duration: '7 days',
    rating: 4.7,
    uses: 1534,
    description: 'Trial user activation and conversion'
  }, {
    id: 4,
    name: 'Inbound Lead Scoring',
    category: 'Inbound',
    touchpoints: 5,
    duration: '10 days',
    rating: 4.6,
    uses: 678,
    description: 'Automated lead qualification and routing'
  }, {
    id: 5,
    name: 'Re-engagement Campaign',
    category: 'Nurture',
    touchpoints: 7,
    duration: '14 days',
    rating: 4.5,
    uses: 543,
    description: 'Win back inactive or churned accounts'
  }, {
    id: 6,
    name: 'Product Launch Sequence',
    category: 'Product Marketing',
    touchpoints: 10,
    duration: '21 days',
    rating: 4.8,
    uses: 421,
    description: 'Coordinated launch campaign across channels'
  }];
  const categories = ['all', 'ABM', 'PLG', 'Inbound', 'Nurture', 'Product Marketing'];
  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  return <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} data-id="element-3298" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-3299">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-3300">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-3301">
            <div data-id="element-3302">
              <h2 className="text-xl font-semibold text-gray-900" data-id="element-3303">
                Play Template Library
              </h2>
              <p className="text-sm text-gray-600" data-id="element-3304">
                Browse and import pre-built GTM plays
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-3305">
              <XIcon className="w-5 h-5 text-gray-500" data-id="element-3306" />
            </button>
          </div>
          {/* Search & Filters */}
          <div className="px-6 py-4 border-b border-gray-200 space-y-4" data-id="element-3307">
            <div className="relative" data-id="element-3308">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-3309" />
              <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent" data-id="element-3310" />
            </div>
            <div className="flex items-center gap-2" data-id="element-3311">
              <FilterIcon className="w-4 h-4 text-gray-600" data-id="element-3312" />
              <div className="flex flex-wrap gap-2" data-id="element-3313">
                {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${selectedCategory === category ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-3314">
                    {category}
                  </button>)}
              </div>
            </div>
          </div>
          {/* Templates Grid */}
          <div className="flex-1 overflow-y-auto p-6" data-id="element-3315">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3316">
              {filteredTemplates.map(template => <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow" data-id="element-3317">
                  <div className="flex items-start justify-between mb-3" data-id="element-3318">
                    <div className="flex-1" data-id="element-3319">
                      <h3 className="font-semibold text-gray-900 mb-1" data-id="element-3320">
                        {template.name}
                      </h3>
                      <p className="text-sm text-gray-600" data-id="element-3321">
                        {template.description}
                      </p>
                    </div>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full" data-id="element-3322">
                      {template.category}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-3 text-sm text-gray-600" data-id="element-3323">
                    <span data-id="element-3324">{template.touchpoints} touchpoints</span>
                    <span data-id="element-3325">•</span>
                    <span data-id="element-3326">{template.duration}</span>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200" data-id="element-3327">
                    <div className="flex items-center gap-3 text-sm" data-id="element-3328">
                      <div className="flex items-center gap-1" data-id="element-3329">
                        <StarIcon className="w-4 h-4 text-yellow-500 fill-current" data-id="element-3330" />
                        <span className="font-medium text-gray-900" data-id="element-3331">
                          {template.rating}
                        </span>
                      </div>
                      <span className="text-gray-600" data-id="element-3332">
                        {template.uses.toLocaleString()} uses
                      </span>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors" data-id="element-3333">
                      <DownloadIcon className="w-4 h-4" data-id="element-3334" />
                      Import
                    </button>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </>;
}